// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AuthContextType {
  isAuthenticated: boolean;
  password: string | null;
  login: (password: string) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Clé pour le localStorage
const STORAGE_KEY = 'oclope_auth_password';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fonction pour valider un mot de passe avec le webhook
  const validatePasswordWithWebhook = async (inputPassword: string): Promise<boolean> => {
    try {
      console.log('[Auth] Validating password with grant-access webhook');
      
      const response = await fetch('https://sms-back.cigarette-electronique-tournefeuille.fr/webhook/grant-access', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Auth-Password': inputPassword.trim(),
          'User-Agent': navigator.userAgent
        },
        body: JSON.stringify({
          password: inputPassword.trim(),
          action: 'login_attempt',
          timestamp: new Date().toISOString(),
          user_agent: navigator.userAgent
        }),
        signal: AbortSignal.timeout(10000) // 10 secondes timeout
      });

      console.log('[Auth] Grant-access response status:', response.status);

      if (!response.ok) {
        console.log('[Auth] HTTP error from grant-access:', response.status);
        return false;
      }

      const result = await response.text();
      console.log('[Auth] Grant-access response:', result);

      // Vérification de la réponse - accepte "OK" (case insensitive) ou un JSON avec success: true
      const resultLower = result.toLowerCase().trim();
      let isGranted = false;

      if (resultLower === 'ok') {
        isGranted = true;
      } else {
        // Tentative de parsing JSON au cas où
        try {
          const jsonResult = JSON.parse(result);
          if (jsonResult.status === 'OK' || jsonResult.success === true || jsonResult.access === 'granted') {
            isGranted = true;
          }
        } catch (e) {
          // Si ce n'est pas du JSON, on considère que c'est refusé
          console.log('[Auth] Response is not JSON, treating as refused');
        }
      }

      return isGranted;
    } catch (error: any) {
      console.error('[Auth] Password validation error:', error);
      return false;
    }
  };

  // Vérifier l'authentification au démarrage (localStorage)
  useEffect(() => {
    const checkStoredAuth = async () => {
      setIsLoading(true);
      
      try {
        const storedPassword = localStorage.getItem(STORAGE_KEY);
        
        if (storedPassword) {
          console.log('[Auth] Found stored password, validating...');
          
          // Valider le mot de passe stocké avec le webhook
          const isValid = await validatePasswordWithWebhook(storedPassword);
          
          if (isValid) {
            console.log('[Auth] Stored password is valid, auto-login successful');
            setPassword(storedPassword);
            setIsAuthenticated(true);
          } else {
            console.log('[Auth] Stored password is invalid, clearing localStorage');
            localStorage.removeItem(STORAGE_KEY);
          }
        } else {
          console.log('[Auth] No stored password found');
        }
      } catch (error) {
        console.error('[Auth] Error checking stored auth:', error);
        // En cas d'erreur, nettoyer le localStorage
        localStorage.removeItem(STORAGE_KEY);
      } finally {
        setIsLoading(false);
      }
    };

    checkStoredAuth();
  }, []);

  const login = async (inputPassword: string): Promise<boolean> => {
    setIsLoading(true);
    
    try {
      if (!inputPassword.trim()) {
        console.log('[Auth] Login failed - empty password');
        return false;
      }

      // Valider le mot de passe avec le webhook
      const isValid = await validatePasswordWithWebhook(inputPassword.trim());

      if (isValid) {
        const trimmedPassword = inputPassword.trim();
        
        // Authentifier l'utilisateur
        setPassword(trimmedPassword);
        setIsAuthenticated(true);
        
        // NOUVEAU: Stocker le mot de passe en localStorage pour la persistance
        localStorage.setItem(STORAGE_KEY, trimmedPassword);
        
        console.log('[Auth] Login successful - Access granted and password stored');
        return true;
      } else {
        console.log('[Auth] Login failed - Access refused by server');
        return false;
      }

    } catch (error: any) {
      console.error('[Auth] Login error:', error);
      
      // Gestion spécifique des erreurs
      if (error.name === 'AbortError') {
        console.log('[Auth] Login timeout - Server took too long to respond');
      } else if (error.message.includes('fetch')) {
        console.log('[Auth] Network error during authentication');
      }
      
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    // Nettoyer l'état
    setIsAuthenticated(false);
    setPassword(null);
    
    // NOUVEAU: Supprimer le mot de passe du localStorage
    localStorage.removeItem(STORAGE_KEY);
    
    console.log('[Auth] Logout successful - localStorage cleared');
  };

  const value: AuthContextType = {
    isAuthenticated,
    password,
    login,
    logout,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};